import { API_URL, fetchWithErrorHandling, fetchWithAuth } from '../utils/api';
import { normalizeUserData, AuthResponse, UserData } from './auth';

// Shared: store the session from a social auth response, or throw.
const finalizeAuth = (response: any): AuthResponse => {
  if (response.token && response.nickname) {
    try {
      const userData = normalizeUserData(response);
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(userData));
      return { token: response.token, user: userData };
    } catch (error) {
      console.error('Error storing social auth data:', error);
      throw new Error('Failed to store authentication data');
    }
  }
  throw new Error('Invalid response from server');
};

export const loginWithGoogle = async (idToken: string): Promise<AuthResponse> => {
  const response = await fetchWithErrorHandling(`${API_URL}/auth/google/mobile`, {
    method: 'POST',
    body: JSON.stringify({ idToken }),
  });
  return finalizeAuth(response);
};

export interface AppleCredential {
  identityToken: string;
  nonce: string;
  fullname?: string;
}

export const loginWithApple = async ({
  identityToken,
  nonce,
  fullname,
}: AppleCredential): Promise<AuthResponse> => {
  const response = await fetchWithErrorHandling(`${API_URL}/auth/apple/mobile`, {
    method: 'POST',
    body: JSON.stringify({ identityToken, nonce, fullname }),
  });
  return finalizeAuth(response);
};

export interface VkCredential {
  code: string;
  deviceId: string;
  state: string;
  codeVerifier: string;
  redirectUri: string;
}

export const loginWithVk = async (credential: VkCredential): Promise<AuthResponse> => {
  const response = await fetchWithErrorHandling(`${API_URL}/auth/vk/mobile`, {
    method: 'POST',
    body: JSON.stringify(credential),
  });
  return finalizeAuth(response);
};

// Link calls are authenticated (fetchWithAuth) and return the updated account.
export const linkVk = async (credential: VkCredential): Promise<UserData> => {
  const response = await fetchWithAuth(`${API_URL}/auth/vk/link`, {
    method: 'POST',
    body: JSON.stringify(credential),
  });
  return normalizeUserData(response);
};

export const linkGoogle = async (idToken: string): Promise<UserData> => {
  const response = await fetchWithAuth(`${API_URL}/auth/google/link`, {
    method: 'POST',
    body: JSON.stringify({ idToken }),
  });
  return normalizeUserData(response);
};

export const linkApple = async ({
  identityToken,
  nonce,
  fullname,
}: AppleCredential): Promise<UserData> => {
  const response = await fetchWithAuth(`${API_URL}/auth/apple/link`, {
    method: 'POST',
    body: JSON.stringify({ identityToken, nonce, fullname }),
  });
  return normalizeUserData(response);
};
